import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {ApiService, IImgUrls} from './api.service';
import {catchError, EMPTY, firstValueFrom, map, of, tap} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {AUTH_KEYS} from './auth-constants';
import {LoadingService} from './loading.service';
import {IDictionary} from './dictionary.service';
import {ISocial} from '../../base/settings/settings-profile/settings-profile-form-blogger/settings-profile-form-blogger.types';

interface ICategory {
  id: number;
  name: string;
  niches: string[];
}

export interface IBloggerProfile {
  type: 'blogger';
  userId: number;
  phone: string;
  firstName: string;
  lastName: string;
  middleName: string;
  fio: string;
  image: IImgUrls;
  categories: ICategory[];
  niches: string[];
  platforms: string[];
  socials: ISocial[];
  workFormats: IDictionary[];
  bloggerType?: 'OOO' | 'IP' | 'SELF_EMPLOYED' | null;
  bloggerTypeInfo?: IBloggerIpTypeInfo | IBloggerOooTypeInfo | IBloggerSelfEmployedTypeInfo | string | null;
  selfEmploymentDoc?: IImgUrls | null;
}

export interface ISellerProfile {
  type: 'seller';
  userId: number;
  phone: string;
  firstName: string;
  lastName: string;
  middleName: string;
  categories: ICategory[];
  description: string;
  marketplaces: any[];
  offerType: string;
  url: string;
  niches: string[];
  sellerType?: 'IP' | 'OOO' | null;
  sellerTypeInfo?: ISellerIpTypeInfo | ISellerOooTypeInfo | string | null;
}

export interface ISellerIpTypeInfo {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  registrationAddress: string | null;
  passportSeries: string | null;
  passportNumber: string | null;
  passportDate: string | null;
  departmentCode: string | null;
  birthDate: string | null;
  birthPlace: string | null;
  inn: string | null;
  accNumber: string | null;
  bik: string | null;
}

export interface ISellerOooTypeInfo {
  organizationName: string | null;
  inn: string | null;
  kpp: string | null;
  ogrn: string | null;
  bik: string | null;
}

export interface IBloggerIpTypeInfo {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  registrationAddress: string | null;
  inn: string | null;
  accNumber: string | null;
  bik: string | null;
}

export interface IBloggerOooTypeInfo {
  organizationName: string | null;
  inn: string | null;
  kpp: string | null;
  ogrn: string | null;
  bik: string | null;
  accNumber: string | null;
}

export interface IBloggerSelfEmployedTypeInfo {
  firstName: string | null;
  lastName: string | null;
  middleName: string | null;
  registrationAddress: string | null;
  passportSeries: string | null;
  passportNumber: string | null;
  passportDate: string | null;
  departmentCode: string | null;
  birthDate: string | null;
  birthPlace: string | null;
  inn: string | null;
  payOutMethod: 'ACCOUNT_NUMBER' | 'CARD' | 'SBP' | null;
  accNumber: string | null;
  bik: string | null;
  phone: string | null;
  cardNumber: string | null;
}

interface IUserBase {
  id: number;
  username: string;
  role: 'BLOGGER' | 'SELLER';
  active: boolean;
  actualBalance: number;
  holdBalance: number;
  authorities: string[];
  is2FaEnabled: boolean;
  lastOnline: string;
  rating: number;
  unreadAmount: number;
  verificationStatus: string;
  verifiedAt: string;
  verifierId: number;
  createdAt: string;
  updatedAt: string;
  denyReason?:string;
}

interface IBloggerUser extends IUserBase {
  role: 'BLOGGER';
  blogger: IBloggerProfile;
  userId: number;
  type: 'blogger';
  phone: string;
  firstName: string;
  lastName: string;
  middleName: string;
  fio: string;
  image: IImgUrls;
  categories: ICategory[];
  niches: string[];
  platforms: string[];
  socials: ISocial[];
  workFormats: IDictionary[];
  bloggerType?: 'OOO' | 'IP' | 'SELF_EMPLOYED' | null;
  bloggerTypeInfo?: IBloggerIpTypeInfo | IBloggerOooTypeInfo | IBloggerSelfEmployedTypeInfo | string | null;
  selfEmploymentDoc?: IImgUrls | null;
}

interface ISellerUser extends IUserBase {
  role: 'SELLER';
  seller: ISellerProfile;
  userId: number;
  type: 'seller';
  phone: string;
  firstName: string;
  lastName: string;
  middleName: string;
  categories: ICategory[];
  description: string;
  marketplaces: any[];
  offerType: string;
  url: string;
  sellerType?: 'IP' | 'OOO' | null;
  sellerTypeInfo?: ISellerIpTypeInfo | ISellerOooTypeInfo | string | null;
}

export type IUser = IBloggerUser | ISellerUser;

@Injectable({providedIn: 'root'})
export class AuthService {
  apiService = inject(ApiService);
  loadingService = inject(LoadingService);
  router = inject(Router)
  private userSignal = signal<IUser | null>(null);
  user = computed(() => this.userSignal());
  isVerified = computed(() => this.user()?.verificationStatus === 'VERIFIED');

  constructor() {
    //console.log("start auth service")
  }

  Register(data: any) {
    return this.apiService.SendPost('/user/register', data, this.loadingService.StatesNames.REGISTER).pipe(
      tap((res) => {
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res[AUTH_KEYS.ACCESS_TOKEN]);
        localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res[AUTH_KEYS.REFRESH_TOKEN]);
        this.userSignal.set(res['user'])
      })
    )
  }

  HasTokens() {
    return localStorage.getItem(AUTH_KEYS.ACCESS_TOKEN) && localStorage.getItem(AUTH_KEYS.REFRESH_TOKEN)
  }

  RequestPassword(data: any) {
    return this.apiService.SendPost('/user/reset-password/request', data, this.loadingService.StatesNames.REQUEST_PASSWORD).pipe(
      tap((res) => {
      })
    )
  }

  RestorePassword(data: any) {
    return this.apiService.SendPost('/user/reset-password/confirm', data, this.loadingService.StatesNames.RESTORE_PASSWORD).pipe(
      tap((res) => {
      })
    )
  }

  RestorePasswordCheck(data: any) {
    return this.apiService.SendPost('/user/reset-password/check', data, this.loadingService.StatesNames.CHECK_PASSWORD_LINK).pipe(
      tap((res) => {
      })
    )
  }

  Login(data: any) {
    return this.apiService.SendPost('/auth', data, this.loadingService.StatesNames.LOGIN).pipe(
      tap((res) => {
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res[AUTH_KEYS.ACCESS_TOKEN]);
        localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res[AUTH_KEYS.REFRESH_TOKEN]);
        this.userSignal.set(res['user']);
      })
    );
  }

  GetMe() {
    return this.apiService.SendGet('/auth/me')
      .pipe(
        catchError((err) => {

          if (!window.location.pathname.includes('/auth')) {
            //console.log("err redirect")
            this.router.navigate(['/auth']);
          }
          this.CLearSession();
          return EMPTY;
        }),
        tap((res) => {
            this.userSignal.set(res);
            if (window.location.href.includes('auth')) {
              this.router.navigate(['/base/briefs/list'])
            } else {
              //console.log("window.location.href.includes('auth')",window.location.href.split('base'))
            }
          }
        )
      )
  }

  awaitUser(): Promise<any> {
    return new Promise((resolve) => {
      const runner = effect(() => {
        const u = this.userSignal();
        if (u !== null && u !== undefined) {
          resolve(u);
          runner.destroy();
        }
      });
    });
  }

  RefreshToken() {
    return this.apiService.SendPost('/auth/refresh', {}).pipe(tap((res) => {
      localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res['token']);
      localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res['refresh']);
      this.userSignal.set(res['data']);
    }))
  }


  CLearSession() {
    localStorage.removeItem(AUTH_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_KEYS.REFRESH_TOKEN);
    this.userSignal.set(null);
  }

  logout() {
    return this.apiService.SendPost('/auth/logout', {}).pipe(
      tap(() => {
        this.CLearSession();
        this.router.navigate(['/auth']);
      })
    )
  }

  updateUser(User: any) {
    const current = this.userSignal();

    const updatedUser: any = {
      ...current,
      ...User,
    };

    if (User.userType) {
      if (User.userType.type === 'blogger') {
        updatedUser.blogger = User.userType;
      } else {
        updatedUser.seller = User.userType;
      }
    }

    this.userSignal.set(updatedUser);
  }
}
